// ─── Field Types ──────────────────────────────────────────────────────────────
import {
  CaseSensitive, Hash, ListChecks, CalendarDays, Text,
  SquareCheckBig, Mail, Smartphone, CircleDollarSign, FileUp,
  CircleDot,
} from 'lucide-react';

// uid is defined inline here to avoid circular dependency with utils.js
function _uid() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

// Wrap Lucide icons with consistent sizing + stroke for modern density
const lu = (Cmp) => (props) => <Cmp size={16} strokeWidth={2} {...props} />;

// `label` is the English fallback; `labelKey` is what the UI should render.
// Always go through fieldTypeLabel(t, info) from utils.js so the tiles, type
// switcher and previews follow the active language.
export const FIELD_TYPES = [
  { type: 'text',     label: 'Text',        labelKey: 'fieldsManager.fieldTypes.text',     icon: lu(CaseSensitive),    color: '#6366f1', bg: '#eef2ff', badge: 'T'  },
  { type: 'number',   label: 'Number',      labelKey: 'fieldsManager.fieldTypes.number',   icon: lu(Hash),             color: '#10b981', bg: '#ecfdf5', badge: '#'  },
  { type: 'select',   label: 'Select',      labelKey: 'fieldsManager.fieldTypes.select',   icon: lu(ListChecks),       color: '#8b5cf6', bg: '#f5f3ff', badge: '▾'  },
  { type: 'radio',    label: 'Radio',       labelKey: 'fieldsManager.fieldTypes.radio',    icon: lu(CircleDot),        color: '#a855f7', bg: '#faf5ff', badge: '◉'  },
  { type: 'date',     label: 'Date',        labelKey: 'fieldsManager.fieldTypes.date',     icon: lu(CalendarDays),     color: '#f59e0b', bg: '#fffbeb', badge: '📅' },
  { type: 'textarea', label: 'Text Area',   labelKey: 'fieldsManager.fieldTypes.textarea', icon: lu(Text),             color: '#3b82f6', bg: '#eff6ff', badge: '¶'  },
  { type: 'checkbox', label: 'Checkbox',    labelKey: 'fieldsManager.fieldTypes.checkbox', icon: lu(SquareCheckBig),   color: '#06b6d4', bg: '#ecfeff', badge: '☑'  },
  { type: 'email',    label: 'Email',       labelKey: 'fieldsManager.fieldTypes.email',    icon: lu(Mail),             color: '#ec4899', bg: '#fdf2f8', badge: '@'  },
  { type: 'phone',    label: 'Phone',       labelKey: 'fieldsManager.fieldTypes.phone',    icon: lu(Smartphone),       color: '#14b8a6', bg: '#f0fdfa', badge: '☎'  },
  { type: 'currency', label: 'Currency',    labelKey: 'fieldsManager.fieldTypes.currency', icon: lu(CircleDollarSign), color: '#f97316', bg: '#fff7ed', badge: '$'  },
  { type: 'file',     label: 'File Upload', labelKey: 'fieldsManager.fieldTypes.file',     icon: lu(FileUp),           color: '#64748b', bg: '#f8fafc', badge: '📎' },
];

export const SECTION_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#3b82f6', '#ec4899', '#14b8a6', '#f97316', '#64748b',
];

export const COLUMN_WIDTHS = ['small', 'medium', 'large'];
export const MAX_HISTORY = 20;

// ─── Preset Templates ─────────────────────────────────────────────────────────
// Templates use _uid() directly to avoid circular dependency

export const PRESET_TEMPLATES = [
  {
    id: 'nda',
    name: 'NDA / Confidentiality',
    icon: '🔒',
    description: 'Non-disclosure agreement fields',
    color: '#6366f1',
    schema: {
      sections: [
        {
          id: _uid(), title: 'Parties & Scope', type: 'fields', color: '#6366f1', collapsed: false, order: 0,
          fields: [
            { id: _uid(), label: 'Disclosing Party', type: 'text', required: true, placeholder: 'Company or individual name', options: [], order: 0 },
            { id: _uid(), label: 'Receiving Party', type: 'text', required: true, placeholder: 'Company or individual name', options: [], order: 1 },
            { id: _uid(), label: 'Purpose of Disclosure', type: 'textarea', required: true, placeholder: 'Describe the purpose...', options: [], order: 2 },
            { id: _uid(), label: 'Confidentiality Period (Years)', type: 'number', required: true, placeholder: '2', options: [], order: 3 },
          ],
        },
        {
          id: _uid(), title: 'Agreement Terms', type: 'fields', color: '#8b5cf6', collapsed: false, order: 1,
          fields: [
            { id: _uid(), label: 'Effective Date', type: 'date', required: true, placeholder: '', options: [], order: 0 },
            { id: _uid(), label: 'Governing Law', type: 'text', required: false, placeholder: 'e.g., State of Delaware', options: [], order: 1 },
            { id: _uid(), label: 'Mutual NDA', type: 'checkbox', required: false, placeholder: '', options: [], order: 2 },
          ],
        },
      ],
    },
  },
  {
    id: 'service',
    name: 'Service Agreement',
    icon: '🤝',
    description: 'Professional services contract',
    color: '#10b981',
    schema: {
      sections: [
        {
          id: _uid(), title: 'Service Details', type: 'fields', color: '#10b981', collapsed: false, order: 0,
          fields: [
            { id: _uid(), label: 'Service Description', type: 'textarea', required: true, placeholder: 'Describe the services...', options: [], order: 0 },
            { id: _uid(), label: 'Service Category', type: 'select', required: true, placeholder: '', options: [
              { id: _uid(), label: 'Consulting' }, { id: _uid(), label: 'Development' },
              { id: _uid(), label: 'Design' }, { id: _uid(), label: 'Marketing' }, { id: _uid(), label: 'Other' },
            ], order: 1 },
            { id: _uid(), label: 'Deliverables', type: 'textarea', required: false, placeholder: 'List key deliverables...', options: [], order: 2 },
          ],
        },
        {
          id: _uid(), title: 'Financial Terms', type: 'fields', color: '#f59e0b', collapsed: false, order: 1,
          fields: [
            { id: _uid(), label: 'Service Fee', type: 'currency', required: true, placeholder: '0.00', options: [], order: 0 },
            { id: _uid(), label: 'Payment Schedule', type: 'select', required: true, placeholder: '', options: [
              { id: _uid(), label: 'Monthly' }, { id: _uid(), label: 'Quarterly' },
              { id: _uid(), label: 'Upon Completion' }, { id: _uid(), label: 'Milestone-based' },
            ], order: 1 },
          ],
        },
        {
          id: _uid(), title: 'Payment Milestones', type: 'table', color: '#3b82f6', collapsed: false, order: 2,
          tableColumns: [
            { id: _uid(), header: 'Milestone', type: 'text', width: 'large', required: true, options: [], order: 0 },
            { id: _uid(), header: 'Due Date', type: 'date', width: 'medium', required: true, options: [], order: 1 },
            { id: _uid(), header: 'Amount', type: 'currency', width: 'medium', required: true, options: [], order: 2 },
            { id: _uid(), header: 'Status', type: 'select', width: 'small', required: false, options: [
              { id: _uid(), label: 'Pending' }, { id: _uid(), label: 'Paid' }, { id: _uid(), label: 'Overdue' },
            ], order: 3 },
          ],
        },
      ],
    },
  },
  {
    id: 'employment',
    name: 'Employment Contract',
    icon: '👤',
    description: 'Employee onboarding fields',
    color: '#f59e0b',
    schema: {
      sections: [
        {
          id: _uid(), title: 'Employee Information', type: 'fields', color: '#f59e0b', collapsed: false, order: 0,
          fields: [
            { id: _uid(), label: 'Job Title', type: 'text', required: true, placeholder: 'e.g., Senior Engineer', options: [], order: 0 },
            { id: _uid(), label: 'Department', type: 'text', required: true, placeholder: 'e.g., Engineering', options: [], order: 1 },
            { id: _uid(), label: 'Employment Type', type: 'select', required: true, placeholder: '', options: [
              { id: _uid(), label: 'Full-time' }, { id: _uid(), label: 'Part-time' },
              { id: _uid(), label: 'Contract' }, { id: _uid(), label: 'Internship' },
            ], order: 2 },
            { id: _uid(), label: 'Start Date', type: 'date', required: true, placeholder: '', options: [], order: 3 },
          ],
        },
        {
          id: _uid(), title: 'Compensation', type: 'fields', color: '#10b981', collapsed: false, order: 1,
          fields: [
            { id: _uid(), label: 'Base Salary', type: 'currency', required: true, placeholder: '0.00', options: [], order: 0 },
            { id: _uid(), label: 'Pay Frequency', type: 'select', required: true, placeholder: '', options: [
              { id: _uid(), label: 'Monthly' }, { id: _uid(), label: 'Bi-weekly' }, { id: _uid(), label: 'Weekly' },
            ], order: 1 },
            { id: _uid(), label: 'Bonus Eligible', type: 'checkbox', required: false, placeholder: '', options: [], order: 2 },
          ],
        },
      ],
    },
  },
  {
    id: 'vendor',
    name: 'Vendor Agreement',
    icon: '🏭',
    description: 'Supplier / vendor contract',
    color: '#ef4444',
    schema: {
      sections: [
        {
          id: _uid(), title: 'Vendor Details', type: 'fields', color: '#ef4444', collapsed: false, order: 0,
          fields: [
            { id: _uid(), label: 'Vendor Category', type: 'select', required: true, placeholder: '', options: [
              { id: _uid(), label: 'Technology' }, { id: _uid(), label: 'Raw Materials' },
              { id: _uid(), label: 'Logistics' }, { id: _uid(), label: 'Professional Services' }, { id: _uid(), label: 'Other' },
            ], order: 0 },
            { id: _uid(), label: 'Products / Services Supplied', type: 'textarea', required: true, placeholder: 'Describe what is being supplied...', options: [], order: 1 },
          ],
        },
        {
          id: _uid(), title: 'Order Details', type: 'table', color: '#f97316', collapsed: false, order: 1,
          tableColumns: [
            { id: _uid(), header: 'Item / SKU', type: 'text', width: 'large', required: true, options: [], order: 0 },
            { id: _uid(), header: 'Quantity', type: 'number', width: 'small', required: true, options: [], order: 1 },
            { id: _uid(), header: 'Unit Price', type: 'currency', width: 'medium', required: true, options: [], order: 2 },
            { id: _uid(), header: 'Delivery Date', type: 'date', width: 'medium', required: false, options: [], order: 3 },
          ],
        },
      ],
    },
  },
  {
    id: 'lease',
    name: 'Lease Agreement',
    icon: '🏢',
    description: 'Property rental / lease fields',
    color: '#3b82f6',
    schema: {
      sections: [
        {
          id: _uid(), title: 'Property Details', type: 'fields', color: '#3b82f6', collapsed: false, order: 0,
          fields: [
            { id: _uid(), label: 'Property Address', type: 'textarea', required: true, placeholder: 'Full address...', options: [], order: 0 },
            { id: _uid(), label: 'Property Type', type: 'select', required: true, placeholder: '', options: [
              { id: _uid(), label: 'Office' }, { id: _uid(), label: 'Retail' },
              { id: _uid(), label: 'Warehouse' }, { id: _uid(), label: 'Residential' }, { id: _uid(), label: 'Industrial' },
            ], order: 1 },
            { id: _uid(), label: 'Area (sq ft)', type: 'number', required: false, placeholder: '0', options: [], order: 2 },
          ],
        },
        {
          id: _uid(), title: 'Lease Terms', type: 'fields', color: '#8b5cf6', collapsed: false, order: 1,
          fields: [
            { id: _uid(), label: 'Monthly Rent', type: 'currency', required: true, placeholder: '0.00', options: [], order: 0 },
            { id: _uid(), label: 'Security Deposit', type: 'currency', required: true, placeholder: '0.00', options: [], order: 1 },
            { id: _uid(), label: 'Lease Start Date', type: 'date', required: true, placeholder: '', options: [], order: 2 },
            { id: _uid(), label: 'Lease End Date', type: 'date', required: true, placeholder: '', options: [], order: 3 },
            { id: _uid(), label: 'Auto-Renewal', type: 'checkbox', required: false, placeholder: '', options: [], order: 4 },
          ],
        },
      ],
    },
  },
  {
    id: 'blank',
    name: 'Start from Scratch',
    icon: '✨',
    description: 'Build your own custom form',
    color: '#64748b',
    schema: {
      sections: [
        {
          id: _uid(), title: 'General Information', type: 'fields', color: '#6366f1', collapsed: false, order: 0,
          fields: [
            { id: _uid(), label: 'Contract Title', type: 'text', required: true, placeholder: '', options: [], order: 0 },
            { id: _uid(), label: 'Effective Date', type: 'date', required: true, placeholder: '', options: [], order: 1 },
          ],
        },
      ],
    },
  },
];
